import { StyleSheet } from 'react-native';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { useThemedStyles } from '@/hooks/residents/useThemeColors';

export function useResidentChatWindowStyles() {
  return useThemedStyles(({ colors }) =>
    StyleSheet.create({
      container: {
        flex: 1,
        backgroundColor: colors.card.bg,
        flexDirection: 'column',
      },
      messagesContainer: {
        flex: 1,
        flexDirection: 'column',
      },
      errorContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 24,
      },
      errorText: {
        ...REDESIGN_TYPOGRAPHY.body,
        color: colors.text.muted,
      },
      emptyContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 24,
      },
      emptyText: {
        ...REDESIGN_TYPOGRAPHY.body,
        color: colors.text.subtle,
        marginTop: 12,
        textAlign: 'center',
      },
      messagesList: {
        flex: 1,
        paddingHorizontal: 16,
      },
      messagesContent: {
        paddingVertical: 16,
        paddingBottom: 20,
      },
      messageGroup: { marginBottom: 16 },
      dateHeader: { alignItems: 'center', marginVertical: 16 },
      dateText: {
        ...REDESIGN_TYPOGRAPHY.caption,
        fontWeight: '300',
        color: colors.text.dim,
        textAlign: 'center',
        marginVertical: 16,
      },
      messageBubble: {
        width: '65%',
        minWidth: 320,
        marginBottom: 8,
        padding: 16,
        borderRadius: 14,
      },
      senderName: {
        ...REDESIGN_TYPOGRAPHY.caption,
        fontWeight: '500',
        color: colors.text.muted,
        marginBottom: 8,
      },
      messageText: {
        ...REDESIGN_TYPOGRAPHY.body,
        color: colors.text.primary,
        lineHeight: 20,
      },
      messageFooter: {
        flexDirection: 'row-reverse' as const,
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        marginTop: 8,
      },
      timeStamp: {
        ...REDESIGN_TYPOGRAPHY.caption,
        fontWeight: '300',
        color: colors.text.imageSubtitle,
      },
      senderNameBottom: {
        ...REDESIGN_TYPOGRAPHY.caption,
        color: colors.text.imageSubtitle,
      },
      fileBubbleContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: colors.glass.buttonBg,
        borderRadius: 10,
        padding: 8,
        marginBottom: 8,
        width: 'auto',
        maxWidth: '66%',
      },
      fileInfo: {
        flexDirection: 'column',
        marginLeft: 8,
        marginRight: 16,
      },
      fileNameText: {
        ...REDESIGN_TYPOGRAPHY.caption,
        fontWeight: 'normal',
        color: colors.text.primary,
        marginBottom: 2,
      },
      fileExtensionText: {
        ...REDESIGN_TYPOGRAPHY.caption,
        fontWeight: '300',
        color: colors.text.dim,
      },
      imageContainer: {
        marginVertical: 8,
        maxWidth: '66%',
      },
      imageBubble: {
        borderRadius: 12,
        overflow: 'hidden',
        backgroundColor: colors.bg.gradientStart,
        padding: 4,
      },
      imagePreview: {
        width: 320,
        maxWidth: '100%',
        borderRadius: 8,
      },
      imagePlaceholder: {
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        backgroundColor: colors.bg.gradientStart,
        width: 320,
        maxWidth: '100%',
        borderRadius: 8,
      },
      imageLabel: {
        ...REDESIGN_TYPOGRAPHY.caption,
        color: colors.text.dim,
        textAlign: 'center',
        marginTop: 8,
      },
      messageInputFooter: {
        backgroundColor: colors.card.bg,
        borderTopWidth: 1,
        borderTopColor: colors.separator,
      },
      fileUploadPreview: {
        paddingHorizontal: 16,
        paddingTop: 16,
        borderBottomWidth: 1,
        borderBottomColor: colors.separator,
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 16,
      },
      inputRowContainer: {
        paddingHorizontal: 16,
        paddingVertical: 16,
      },
      inputRow: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        gap: 16,
      },
      paperclipButtonWrapper: {
        alignSelf: 'center',
      },
      paperclipButton: {
        padding: 8,
        borderRadius: 8,
      },
      messageInput: {
        flex: 1,
        ...REDESIGN_TYPOGRAPHY.body,
        color: colors.text.primary,
        backgroundColor: colors.glass.buttonBg,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: colors.glass.buttonBorder,
        paddingHorizontal: 12,
        paddingVertical: 8,
        maxHeight: 120,
        minHeight: 40,
      },
      sendButtonContainer: {
        minHeight: 48,
        borderRadius: 10,
        backgroundColor: colors.glass.buttonBg,
        alignItems: 'center',
        justifyContent: 'center',
      },
      sendButtonContainerActive: {
        backgroundColor: PRIMITIVE_COLORS.resioBlue,
      },
      sendButton: {
        paddingHorizontal: 16,
        paddingVertical: 8,
      },
      sendButtonText: {
        ...REDESIGN_TYPOGRAPHY.caption,
        fontWeight: '500',
        color: colors.text.primary,
      },
      fileUploadBubble: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: colors.glass.buttonBg,
        borderRadius: 10,
        padding: 8,
        width: 'auto',
      },
      fileUploadInfo: {
        flexDirection: 'column',
        marginLeft: 8,
        marginRight: 16,
      },
      fileUploadName: {
        ...REDESIGN_TYPOGRAPHY.caption,
        fontWeight: 'normal',
        color: colors.text.primary,
        marginBottom: 2,
      },
      fileUploadExtension: {
        ...REDESIGN_TYPOGRAPHY.caption,
        fontWeight: '300',
        color: colors.text.dim,
      },
      imageUploadBubble: {
        position: 'relative',
        width: 100,
        height: 100,
      },
      imageUploadPreview: {
        width: '100%',
        height: '100%',
        borderRadius: 8,
      },
      imageUploadPlaceholder: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: colors.bg.gradientStart,
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 8,
      },
      deleteImageButton: {
        position: 'absolute',
        top: -4,
        right: -4,
        backgroundColor: 'rgba(0,0,0,0.7)',
        borderRadius: 12,
        padding: 4,
      },
      fileUploadDropdown: {
        backgroundColor: colors.card.bg,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: colors.card.border,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.2,
        shadowRadius: 12,
        elevation: 8,
        overflow: 'hidden',
      },
      fileUploadDropdownHeader: {
        paddingHorizontal: 16,
        paddingVertical: 12,
        borderBottomWidth: 1,
        borderBottomColor: colors.separator,
        backgroundColor: colors.card.bg,
      },
      fileUploadDropdownTitle: {
        ...REDESIGN_TYPOGRAPHY.cardTitleLarge,
        color: colors.text.primary,
      },
      fileUploadDropdownContent: {
        flex: 1,
        paddingBottom: 8,
        maxHeight: 520,
      },
      fileUploadSubtitle: {
        ...REDESIGN_TYPOGRAPHY.body,
        color: colors.text.dim,
        paddingHorizontal: 16,
        paddingTop: 8,
      },
    }),
  );
}

export default useResidentChatWindowStyles;
